// Import core lib dependencies.
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import Immutable from 'seamless-immutable';

// Other component dependencies.
import OverlayInterface from '../../apps/common/Overlay/index.js';
import * as overlayDisplayItems from '../../common/components/displayItems/overlays.jsx';
import * as VideoPlayers from '../../common/components/videoPlayers.jsx';

// Model entities.
import DisplayItem from '../../common/model/entity/DisplayItem/DisplayItem.js';

// Misc dependencices.
import { UCFirst } from './utils.js';

// Factories.
import { createDisplayItemEntityFromJSON } from '../../factories/displayItem.js';

/**
 * Add a script tag with external script (returns an HTMLElement)
 * @param {string} url
 * @param {string} id
 * @param {Function} callback
 * @returns {Dom element}
 */
export function addScriptTag(url, id, callback) {
  var el, js;
  if (!url || !id || document.getElementById(id)) {
    return false;
  }
  js = document.createElement('script');
  js.id = id;
  js.src = url;
  js.async = true;
  js.type = 'text/javascript';
  if (typeof callback === 'function') {
    js.onload = function() {
      // TODO: add better event listeners here :P
      callback.call(window);
    };
  }
  el = document.getElementsByTagName('script')[0];
  el.parentNode.insertBefore(js, el);
  return js;
}

/**
 * Add a script tag with inline script (returns an HTMLElement)
 * @param {string} content
 * @param {string} id - dom ID for the script tag
 * @param {Function} callback
 * @param {boolean} replace - whether to replace the dom element if matching one is found
 * @returns {Dom element} and optionally triggers a callback
 */
export function addInlineScript(content, id, callback, replace = false) {
  var el, js, exist;
  if (!content || !id) {
    return false;
  }
  exist = document.getElementById(id);

  if (exist) {
    exist.parentNode.removeChild(exist);
  }

  if (!exist || (exist && replace)) {
    js = document.createElement('script');
    js.type = 'text/javascript';
    js.id = id;
    js.text = content;
    if (typeof callback === 'function') {
      callback.call(root);
    }
    el = document.querySelector('.xPage');
    el.parentNode.insertBefore(js, null);
    return js;
  }
}

export function createCloseButtons(type, action = 'exec:statePrev', isButton) {
  var d = document;

  var toolbar = d.createElement('div'),
    outer,
    middle;

  toolbar.setAttribute('class', 'xActionContainer xActionClose has' + type.charAt(0).toUpperCase() + type.slice(1));

  if (isButton) {
    // Allow main close button to be hidden independently of close icon.
    toolbar.className += ' hasButton';
    outer = d.createElement('div');
    middle = d.createElement('span');
    outer.setAttribute('class', 'xButton xAction xActionCloseOverlay');
    //outer.setAttribute('type','button');
    middle.innerHTML = NGX.lang.get('buttons.overlayClose');
  } else {
    // Create <a> based close button
    outer = d.createElement('a');
    middle = d.createElement('i');
    outer.setAttribute('class', 'xIconButton xAction xActionCloseOverlay');
    outer.setAttribute('href', '#');
    middle.setAttribute('class', 'xIcon xIconClose');
  }

  if (type === 'state' || type === 'promo') {
    // Add ngx-action for reverting to previous state in stateAsOverlay
    middle.setAttribute('data-ngx-action', action);
  }

  if (outer) {
    outer.appendChild(middle);
    toolbar.appendChild(outer);
  }

  return toolbar;
}

/**
 * Go back up the DOM until we hit the first matching tagName (defaults to FORM)
 * @param {Dom element} el
 * @param {string} tagName
 * @returns {*}
 */
export function getParentByTagName(el, tagName) {
  tagName = tagName ? tagName.toUpperCase() : 'FORM';
  if (el && el.nodeType) {
    do {
      if (el.tagName === tagName) {
        return el;
      }
    } while ((el = el.parentNode));
  }
  return false;
}

/**
 * Go back up the DOM until we get a matching attribute and return its value (String or False)
 * @param {Dom element} el
 * @param {string} attr
 * @returns {string|boolean}
 */
export function getParentAttr(el, attr) {
  if (el && attr) {
    do {
      if (_.isElement(el) && el.hasAttribute(attr)) {
        return el.getAttribute(attr);
      }
    } while ((el = el.parentNode));
  }
  return false;
}

/**
 * Get actual offsetTop of specified element (returns a number)
 * @param {Dom element} el
 * @returns {integer}
 */
export function offsetTop(el) {
  var top = 0;
  if (!_.isElement(el)) {
    return false;
  }
  if (el.offsetParent) {
    do {
      top += el.offsetTop;
    } while ((el = el.offsetParent));
  }
  return top;
}

/**
 * Update element class with contextual [prefix][value] class (return class list)
 * will replace any matched prefix'd item
 * @param {Dom element} el
 * @param {string} prefix
 * @param {string} val
 * @returns {Array|string|boolean}
 */
export function updateClassByPrefix(el, prefix, val) {
  var clss = el.getAttribute('class'),
    out = [];
  // don't let it proceed without these, bad things happen.
  if (!val || !prefix) {
    return false;
  }
  if (clss) {
    _.each(clss.split(' '), function(item) {
      if (item.indexOf(prefix) !== 0 && item !== '') {
        out.push(item);
      }
    });
  }
  out.push(prefix + NGX.Util.capitalize(val + ''));
  el.setAttribute('class', out.join(' '));
  return out;
}

/**
 * Return an object containing all attributes beginning with the prefix provided
 * @param {Dom element} el
 * @param {string} prefix
 * @returns {*}
 */
export function getAttrsByPrefix(el, prefix) {
  var out = {},
    plen,
    len,
    attr,
    i = 0;
  if (!el || !prefix || 'undefined' === typeof el.attributes) {
    return false;
  }
  len = el.attributes.length;
  plen = prefix.length;
  for (; i < len; i++) {
    attr = el.attributes[i];
    if (attr.name.indexOf(prefix) === 0) {
      out[attr.name.substring(plen)] = attr.value;
    }
  }
  return out;
}

/**
 * Loads an item from embedded JSON
 * @param {object} item - item config parsed from JSON
 */

export function popOverlayFromJSON(item, options = null) {
  // Proptype checking against React components is now much more strict, we must create a DisplayItem instance before
  // passing it to the component.
  const displayItem = createDisplayItemEntityFromJSON(item);

  // These now need to be injected into the item component (post CS2 branch merge).
  const assetUrl = NGX.context.assetUrl;
  const assetUgcUrl = NGX.context.ugcAssetPath;

  createReactOverlayFromDisplayItem({
    template:    options && options.details ? options.details : 'overlayMediacard',
    item:        displayItem,
    itemOpts:    item.itemOpts || {},
    assetUrl:    assetUrl,
    assetUgcUrl: assetUgcUrl,
  });
}

/**
 * Load item in an overlay
 * @params {string|integer} itemId : ngx content item ID
 * @params {Object} [component]
 */

export function popOverlayForDisplayItem(itemId, component) {
  NGX.App.api.messageParent({ action: 'getScrollPosition' });

  var theItem = null,
    theType = 'social',
    targetState,
    firstCmp = null,
    firstTarget;

  /**
   * Attempts to locate a suitable component on the page to use config from
   * @param {Array} compTypes
   * @returns {*|boolean}
   */
  var findFirstComponent = function(compTypes) {
    log.debug('Searching components for ' + compTypes, 5);
    var _components = [];
    _.each(NGX.App.Components.models, function(cmp) {
      if (_.contains(compTypes, cmp.get('type'))) {
        _components.push(cmp);
      }
    });

    return _components[0] || false;
  };

  const contentServicesVersion = NGX.App.config.contentServiceVersion;
  const experienceVersion = NGX.App.config.appVersion;

  let itemEndpointUrl;
  if (contentServicesVersion === 'v2') {
    const apiKey = NGX.context.apikey;
    const CSAPIHost = NGX.App.config.displayContServiceV2URLsecure;
    itemEndpointUrl = CSAPIHost + '/api/content/3/displayItem?key=' + apiKey + '&id=' + itemId;
  } else {
    itemEndpointUrl = '/display/api/content/displayItem/' + itemId + '.json';
  }

  $.getJSON(itemEndpointUrl)
    .success(function(response) {
      if (response.count === 1) {
        log.debug('The item ID brought back a valid display item', 5);
        theItem = response.content[0];
        theType = theItem.network === 'ugc' ? 'gallery' : 'social';
      } else {
        log.debug('The item ID did not bring back a valid display item, maybe it\'s unpublished?', 5);
      }
    })
    .done(function() {
      if (theType === 'gallery') {
        log.debug('The item is UGC; if the component wasn\'t passed, let\'s find a gallery', 5);
        firstCmp = component || findFirstComponent(['entries']);
        theItem.voteButtonText = NGX.lang.button && NGX.lang.button.prevote ? NGX.lang.button.prevote : 'Vote';
        theItem.entryVotes = theItem.score || '0';
      } else {
        log.debug('The item is social, let\'s find a social component', 5);
      }

      if (!firstCmp)
        firstCmp =
          component || findFirstComponent(['socialcontentwall', 'socialcontentmosaic', 'socialcontentcarousel']);

      if (theItem) {
        if (firstCmp) {
          var tOpts = firstCmp.get('templateOpts');
          firstTarget = $(firstCmp.get('targ')).parents('.xSection');
          targetState = !component ? firstTarget.attr('data-statename') : null;
          // Todo: use proper default state mechanism
          theItem.itemOpts = firstCmp.get('itemOpts');
          theItem.moduleOpts = firstCmp.get('moduleOpts');
          theItem.templateOpts = tOpts;
        }

        // If the component is on a different tab, route to that tab first
        if (targetState && _.findWhere(flowJSON.states, { name: targetState })) {
          const matchingTargetState = _.findWhere(flowJSON.states, { name: targetState });
          log.debug('Attempting to route to state:' + matchingTargetState.route, 5);
          NGX.App.api.action('route:' + matchingTargetState.route);
        }

        var moduleOpts = component ? component.get('moduleOpts') : { ctaDisplayItemRender: 'overlay' };

        // Default values for various template/item/module option objects.
        let templateOpts = {};
        let itemOpts = {};
        // let moduleOpts = {};

        // If we are using the new React/Redux based UgcWalls, then request the config from there.
        if (NGX.App.config.useNewUGCWalls && NGXD.api.UgcWall && Object.keys(NGXD.api.UgcWall.getConfig()).length > 0) {
          const ugcWallConfigs = NGXD.api.UgcWall.getConfig();
          const ugcWallConfig = ugcWallConfigs[Object.keys(ugcWallConfigs)[0]];
          templateOpts = ugcWallConfig.templateOpts;
          itemOpts = ugcWallConfig.itemOpts;
          moduleOpts = ugcWallConfig.moduleOpts;

          // Otherwise default to using the config from the Backbone modules of the old Social/Content walls/modules.
        } else {
          templateOpts = theItem.templateOpts || {};
          itemOpts = theItem.itemOpts || {};
          moduleOpts = theItem.moduleOpts || {};

          if (itemOpts.sharingMode !== 'nosharing' && itemOpts.sharingMode !== '') {
            itemOpts.displaySharing = true;
          }

          if (itemOpts.enableVoting) {
            itemOpts.displayVoting = true;
          }

          if (itemOpts.showVoteCount === 'never') {
            itemOpts.showVoteCount = false;
          }
        }

        const theTemplate = templateOpts.details === 'content' ? 'overlayContentcard' : 'overlayMediacard';

        // Proptype checking against React components is now much more strict, we must create a DisplayItem instance before
        // passing it to the component.
        let displayItem = createDisplayItemEntityFromJSON(theItem);

        // React components will be expecting an Immutable js DisplayObject instance.
        displayItem = Immutable(displayItem, { prototype: DisplayItem.prototype });

        // These now need to be injected into the item component (post CS2 branch merge).
        const assetUrl = NGX.context.assetUrl;
        const assetUgcUrl = NGX.context.ugcAssetPath;

        createReactOverlayFromDisplayItem({
          item:         displayItem,
          template:     theTemplate,
          templateOpts: templateOpts,
          itemOpts:     itemOpts,
          moduleOpts:   moduleOpts,
          onLoad:       function() {
            // TODO: Load handler, to replicate NGX.overlay's callback
            NGX.Insights.track('view:contentitem', { i: itemId });
          },
          assetUrl:    assetUrl,
          assetUgcUrl: assetUgcUrl,
        });
      }
    });
}

export function createReactOverlayFromDisplayItem(options) {
  // Define the Item component class to render.
  const _itemOverlayClassName = UCFirst(options.template);
  const _itemOverlayClass = overlayDisplayItems[_itemOverlayClassName];

  // Define children.
  const _children = <_itemOverlayClass id={options.item.id} item={options.item} {...options} />;

  // Create React overlay.
  createReactOverlay(_children);
}

export function createReactOverlayFromDomNode(theNode) {
  // Define children.
  const _parent = document.createElement('div');
  _parent.appendChild(theNode);
  const _children = _parent.innerHTML;

  // Create React overlay.
  createReactOverlay(_children);
}

export function createReactOverlayFromString(theString) {
  // Define children.
  const _children = theString.toString();

  // Create React overlay.
  createReactOverlay(_children);
}

export function createReactOverlay(children, classes) {
  // Create target element for React components.
  const reactContainer = document.createElement('div');
  document.body.appendChild(reactContainer);

  // Define close handler.
  const closeHandler = function() {
    ReactDOM.unmountComponentAtNode(reactContainer);
    document.body.removeChild(reactContainer);
  };

  // Define React components.
  const _overlay = (
    <Provider store={NGXD.getStore()}>
      <OverlayInterface.Components.Overlay closeHandler={closeHandler} children={children} classes={classes} />
    </Provider>
  );

  // Render React components.
  ReactDOM.render(_overlay, reactContainer);
}

// Mount a video player to a designated dom node.
export function mountVideoPlayer(type, playlist, mountNode) {
  let valid = true;

  // Do some basic sanity checking.
  if (type !== 'stream') {
    console.error('ERROR: unsupported video player type. Type must be "stream".');
    valid = false;
  }
  if (typeof playlist !== 'object') {
    console.error('ERROR: video playlist must be an object.');
    valid = false;
  }
  if (typeof playlist === 'object') {
    if (!playlist.main) {
      console.error('ERROR: video playlist must contain a "main" key.');
      valid = false;
    }
  }
  if (mountNode instanceof Element !== true) {
    console.error('ERROR: invalid dom element reference supplied for mounting video player.');
    valid = false;
  }

  if (!valid) {
    return false;
  }

  // Simulate full asset ref.
  const generateMuxAssetRefFromPlaybackId = function(playbackId) {
    let assetRef;
    if (playbackId) {
      assetRef = 'mux:video:auto:' + playbackId + ':status=ready';
    }

    return assetRef;
  };

  // Simulate required props.
  const item = {
    assets: {
      main: generateMuxAssetRefFromPlaybackId(playlist.main),
    },
  };
  const itemOpts = {
    preRollVideoAssetRef:  generateMuxAssetRefFromPlaybackId(playlist.pre) || null,
    postRollVideoAssetRef: generateMuxAssetRefFromPlaybackId(playlist.post) || null,
  };

  // Define React components.
  const video = (
    <Provider store={NGXD.getStore()}>
      <VideoPlayers.Stream item={item} itemOpts={itemOpts} />
    </Provider>
  );

  // Render React components.
  ReactDOM.render(video, mountNode);
}
